import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../lib/toast';
import { Modal, Field, inputCls } from '../lib/Modal';
import { getToken } from '../api';

// ─── Types ─────────────────────────────────────────────────────────────────
type ScrapedPdf = {
  sha256: string;
  category: string;          // I/U/E/M/P/D/B/F (install / user / spec / etc.)
  decision: string | null;
  page_count: number | null;
  gc_numbers: string[];
  source_url: string | null;
  url: string;               // /api/boiler-manuals/manual/<sha>
};
type Boiler = {
  id: number;
  manufacturer: string;
  model_name: string;
  model_number: string;
  fuel_type: string;
  boiler_type: string;
  status: 'current' | 'discontinued' | string;
  output_range_kw: string;
  efficiency_rating: number | null;
  enabled: boolean;
  gc_numbers: string[];
  curated_pdf_url: string | null;
  curated_pdf_filename: string | null;
  manufacturer_url: string | null;
  notes: string;
  scraped_pdfs: ScrapedPdf[];
};
type AdminPayload = {
  total: number;
  enabled: number;
  manufacturers: string[];
  groups: Record<string, Boiler[]>;
  r2_configured: boolean;
};

// ─── Auth-aware fetch helpers — calls /onlytrade-api/boiler-manuals/admin ──
// admin.onlytrade.ai nginx proxies /onlytrade-api/* → 127.0.0.1:3004/api/*
// (the OnlyTrade backend, where the boiler-manuals route + admin sub-router live).
// /api/* on this domain goes to 3003 (kommercio-admin) — not where our route is.
const ADMIN_BASE = '/onlytrade-api/boiler-manuals/admin';

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
async function aget<T = unknown>(p = ''): Promise<T> {
  const r = await fetch(ADMIN_BASE + p, { headers: authHeaders() });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}
async function apatch<T = unknown>(p: string, body: unknown): Promise<T> {
  const r = await fetch(ADMIN_BASE + p, {
    method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}
async function aput<T = unknown>(p: string, body: unknown): Promise<T> {
  const r = await fetch(ADMIN_BASE + p, {
    method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}
async function apost<T = unknown>(p: string, body?: unknown): Promise<T> {
  const r = await fetch(ADMIN_BASE + p, {
    method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}
async function adelete<T = unknown>(p: string): Promise<T> {
  const r = await fetch(ADMIN_BASE + p, { method: 'DELETE', headers: authHeaders() });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}
async function aupload<T = unknown>(p: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(ADMIN_BASE + p, { method: 'POST', headers: authHeaders(), body: fd });
  if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
  return r.json();
}

// ─── GC number formatting ─────────────────────────────────────────────────
function formatGc(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 7);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return digits.slice(0, 2) + '-' + digits.slice(2);
  return digits.slice(0, 2) + '-' + digits.slice(2, 5) + '-' + digits.slice(5);
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function BoilerManualsPage() {
  const [data, setData] = useState<AdminPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [openMfr, setOpenMfr] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<Boiler | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { setData(await aget<AdminPayload>('')); }
    catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return data;
    const groups: Record<string, Boiler[]> = {};
    for (const mfr of data.manufacturers) {
      const matches = (data.groups[mfr] || []).filter(b =>
        b.model_name.toLowerCase().includes(q) ||
        b.model_number.toLowerCase().includes(q) ||
        mfr.toLowerCase().includes(q) ||
        (b.gc_numbers || []).some(g => g.toLowerCase().includes(q))
      );
      if (matches.length) groups[mfr] = matches;
    }
    return { ...data, groups, manufacturers: Object.keys(groups).sort() };
  }, [data, filter]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Boiler Manuals</h1>
          {data && (
            <div className="text-xs text-ink-muted mt-1 flex items-center gap-2 flex-wrap">
              <span>{data.total} models · {data.enabled} enabled · {data.manufacturers.length} manufacturers</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${data.r2_configured ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'}`}>
                {data.r2_configured ? 'R2 ready' : 'R2 not configured'}
              </span>
            </div>
          )}
        </div>
        <button onClick={() => setAdding(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-3 sm:px-4 py-2 rounded transition-colors whitespace-nowrap">
          + Add Model
        </button>
      </div>

      <input
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Search make, model, model number, GC number…"
        className="w-full px-3 py-2 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600 mb-4"
      />

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          Failed to load: {error}
        </div>
      )}
      {data === null && !error && (
        <div className="text-ink-muted text-sm">Loading boiler manuals…</div>
      )}

      {filtered && filtered.manufacturers.map(mfr => {
        const list = filtered.groups[mfr];
        const open = openMfr[mfr] ?? !!filter;
        const enabledCount = list.filter(b => b.enabled !== false).length;
        const curatedCount = list.filter(b => !!b.curated_pdf_url).length;
        const allEnabled = enabledCount === list.length;
        return (
          <div key={mfr} className="mb-3 bg-ink-card border border-ink-border rounded">
            <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-ink-hover transition-colors gap-3">
              <button onClick={() => setOpenMfr(s => ({ ...s, [mfr]: !open }))}
                      className="flex items-center gap-3 flex-wrap text-left flex-1 min-w-0">
                <span className="text-brand-600 text-sm">{open ? '▾' : '▸'}</span>
                <span className="font-semibold text-sm uppercase tracking-wide">{mfr}</span>
                <span className="text-xs text-ink-muted">{enabledCount}/{list.length} enabled</span>
                {curatedCount > 0 && <span className="text-xs text-emerald-400">{curatedCount} curated PDF{curatedCount === 1 ? '' : 's'}</span>}
              </button>
              <ManufacturerToggle mfr={mfr} count={list.length} allEnabled={allEnabled} onChanged={load} />
            </div>
            {open && (
              <div className="border-t border-ink-border overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-ink-bg/50 text-ink-muted text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Model</th>
                      <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Model #</th>
                      <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">GC Numbers</th>
                      <th className="text-left px-3 py-2 font-medium">Manuals</th>
                      <th className="text-center px-3 py-2 font-medium">On</th>
                      <th className="text-right px-3 py-2 font-medium">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(b => (
                      <BoilerRow key={b.id} b={b} onChanged={load} onEdit={() => setEditing(b)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {filtered && filtered.manufacturers.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          No matches for "{filter}".
        </div>
      )}

      <EditModal
        open={!!editing}
        boiler={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void load(); }}
      />
      <AddModal
        open={adding}
        manufacturers={data?.manufacturers || []}
        onClose={() => setAdding(false)}
        onSaved={() => { setAdding(false); void load(); }}
      />
    </div>
  );
}

// ─── Add modal — minimal: pick mfr + model, optional GC numbers ────────────
function AddModal({ open, manufacturers, onClose, onSaved }: {
  open: boolean;
  manufacturers: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mfr, setMfr] = useState('');
  const [mfrCustom, setMfrCustom] = useState('');
  const [model, setModel] = useState('');
  const [modelNum, setModelNum] = useState('');
  const [type, setType] = useState('combi');
  const [fuel, setFuel] = useState('natural_gas');
  const [status, setStatus] = useState('current');
  const [outputKw, setOutputKw] = useState('');
  const [gcInput, setGcInput] = useState('');
  const [gcs, setGcs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!open) {
      setMfr(''); setMfrCustom(''); setModel(''); setModelNum('');
      setType('combi'); setFuel('natural_gas'); setStatus('current');
      setOutputKw(''); setGcInput(''); setGcs([]);
    }
  }, [open]);

  function addGc() {
    const v = formatGc(gcInput);
    if (!v) return;
    setGcs(s => [...new Set([...s, v.toUpperCase()])]);
    setGcInput('');
  }
  function removeGc(g: string) { setGcs(s => s.filter(x => x !== g)); }

  async function save() {
    const finalMfr = mfr === '__new__' ? mfrCustom.trim() : mfr.trim();
    if (!finalMfr || !model.trim()) {
      toast.push('Manufacturer and Model name required', 'error');
      return;
    }
    setBusy(true);
    try {
      await apost('', {
        manufacturer: finalMfr,
        model_name: model.trim(),
        model_number: modelNum.trim(),
        boiler_type: type,
        fuel_type: fuel,
        status,
        output_range_kw: outputKw.trim(),
        gc_numbers: gcs
      });
      toast.push('Model added — live on website', 'success');
      onSaved();
    } catch (e) { toast.push('Add failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} title="Add Boiler Model" onClose={onClose} width={580}>
      <Field label="Manufacturer">
        <select value={mfr} onChange={e => setMfr(e.target.value)} className={inputCls}>
          <option value="">— Select manufacturer —</option>
          {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
          <option value="__new__">+ New manufacturer…</option>
        </select>
        {mfr === '__new__' && (
          <input value={mfrCustom} onChange={e => setMfrCustom(e.target.value)}
                 placeholder="e.g. Acme Boilers"
                 className={inputCls + ' mt-2'} />
        )}
      </Field>

      <Field label="Model Name" hint="how engineers see it on the website">
        <input value={model} onChange={e => setModel(e.target.value)}
               placeholder="Greenstar 2000 25kW Combi"
               className={inputCls} />
      </Field>

      <Field label="Model Number">
        <input value={modelNum} onChange={e => setModelNum(e.target.value)}
               placeholder="7736902099 / GR2301iW 25C"
               className={inputCls + ' font-mono'} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <select value={type} onChange={e => setType(e.target.value)} className={inputCls}>
            <option value="combi">Combi</option>
            <option value="system">System</option>
            <option value="heat_only">Heat-only / Regular</option>
          </select>
        </Field>
        <Field label="Fuel">
          <select value={fuel} onChange={e => setFuel(e.target.value)} className={inputCls}>
            <option value="natural_gas">Natural Gas</option>
            <option value="lpg">LPG</option>
            <option value="oil">Oil</option>
            <option value="electric">Electric</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Status">
          <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
            <option value="current">Current</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </Field>
        <Field label="Output (kW)" hint="e.g. 4.8-25">
          <input value={outputKw} onChange={e => setOutputKw(e.target.value)}
                 placeholder="4.8-25" className={inputCls} />
        </Field>
      </div>

      <Field label="GC Numbers" hint="optional — auto-formats to XX-XXX-XX">
        <div className="flex gap-2 mb-2">
          <input value={gcInput}
                 onChange={e => setGcInput(formatGc(e.target.value))}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGc(); } }}
                 placeholder="47-044-78"
                 className={inputCls + ' font-mono'} />
          <button onClick={addGc} type="button"
                  className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-3 py-2 rounded">Add</button>
        </div>
        <div className="flex gap-1.5 flex-wrap min-h-[24px]">
          {gcs.map(g => (
            <span key={g} className="font-mono text-xs bg-brand-600/15 text-brand-400 rounded px-2 py-1 flex items-center gap-1.5">
              {g}
              <button onClick={() => removeGc(g)} className="text-brand-400/70 hover:text-red-400">×</button>
            </span>
          ))}
        </div>
      </Field>

      <div className="text-xs text-emerald-400/90 bg-emerald-950/30 border border-emerald-900/50 rounded p-2 my-2">
        ✓ This model will be <b>live on the public website</b> immediately. Toggle off later if needed.
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="text-sm text-ink-muted hover:text-ink-text px-3 py-2">Cancel</button>
        <button onClick={save} disabled={busy}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-50">
          {busy ? 'Adding…' : 'Add & Activate'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Manufacturer master toggle (bulk on/off for every model under a mfr) ──
function ManufacturerToggle({ mfr, count, allEnabled, onChanged }: {
  mfr: string; count: number; allEnabled: boolean; onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function flip(e: React.MouseEvent) {
    e.stopPropagation();
    const next = !allEnabled;
    if (!next && !confirm(`Disable ALL ${count} ${mfr} models? They'll vanish from the public site until re-enabled.`)) return;
    setBusy(true);
    try {
      const r = await apost<{ changed: number }>('/manufacturer/toggle', { manufacturer: mfr, enabled: next });
      toast.push(`${mfr}: ${next ? 'enabled' : 'disabled'} ${r.changed}/${count} models`, 'success');
      onChanged();
    } catch (e2) { toast.push('Bulk toggle failed: ' + (e2 as Error).message, 'error'); }
    finally { setBusy(false); }
  }
  return (
    <button onClick={flip} disabled={busy} title={allEnabled ? `Disable all ${count} ${mfr} models` : `Enable all ${count} ${mfr} models`}
            className={`relative inline-flex h-5 w-10 rounded-full transition-colors shrink-0 ${allEnabled ? 'bg-brand-600' : 'bg-ink-border'} ${busy ? 'opacity-50' : ''}`}
            aria-label={allEnabled ? 'Disable all' : 'Enable all'}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${allEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}/>
    </button>
  );
}

// ─── Single boiler row ────────────────────────────────────────────────────
function BoilerRow({ b, onChanged, onEdit }: { b: Boiler; onChanged: () => void; onEdit: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      await apatch(`/${b.id}`, { enabled: !b.enabled });
      toast.push(b.enabled ? 'Disabled — hidden from public site' : 'Enabled', 'success');
      onChanged();
    } catch (e) { toast.push('Toggle failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function uploadFile(f: File) {
    setBusy(true);
    try {
      await aupload(`/${b.id}/pdf`, f);
      toast.push('PDF uploaded to R2', 'success');
      onChanged();
    } catch (e) { toast.push('Upload failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function removePdf() {
    if (!confirm('Remove the curated PDF for this model?')) return;
    setBusy(true);
    try {
      await adelete(`/${b.id}/pdf`);
      toast.push('Curated PDF removed', 'success');
      onChanged();
    } catch (e) { toast.push('Remove failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  const scrapedCount = (b.scraped_pdfs || []).length;

  return (
    <tr className="border-t border-ink-border/60 hover:bg-ink-hover/40 transition-colors">
      <td className="px-3 py-2.5">
        <div className="font-medium">{b.model_name}</div>
        <div className="text-xs text-ink-muted">{b.boiler_type} · {b.fuel_type} · {b.output_range_kw} kW · {b.status}</div>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs hidden md:table-cell text-ink-muted">{b.model_number || '—'}</td>
      <td className="px-3 py-2.5 hidden lg:table-cell">
        {b.gc_numbers && b.gc_numbers.length
          ? <div className="flex gap-1 flex-wrap max-w-[260px]">{b.gc_numbers.slice(0, 6).map(g =>
              <span key={g} className="font-mono text-xs bg-brand-600/15 text-brand-400 rounded px-1.5 py-0.5">{g}</span>)}
              {b.gc_numbers.length > 6 && <span className="text-xs text-ink-muted">+{b.gc_numbers.length - 6}</span>}
            </div>
          : <span className="text-ink-muted text-xs">none</span>}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-col gap-1">
          {b.curated_pdf_url ? (
            <div className="flex gap-2 items-center">
              <a href={b.curated_pdf_url} target="_blank" rel="noopener" className="text-emerald-400 hover:underline text-xs font-semibold">
                ★ Curated PDF
              </a>
              <button onClick={removePdf} disabled={busy} className="text-xs text-red-400 hover:text-red-300">remove</button>
            </div>
          ) : (
            <label className={`text-xs cursor-pointer ${busy ? 'text-ink-muted' : 'text-brand-400 hover:text-brand-300'}`}>
              + Upload curated PDF
              <input type="file" accept="application/pdf" disabled={busy}
                     onChange={e => { const f = e.target.files?.[0]; if (f) void uploadFile(f); e.currentTarget.value = ''; }}
                     className="hidden" />
            </label>
          )}
          {scrapedCount > 0 && (
            <span className="text-xs text-ink-muted">{scrapedCount} scraped manual{scrapedCount === 1 ? '' : 's'}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-center">
        <button onClick={toggle} disabled={busy}
                className={`relative inline-flex h-5 w-10 rounded-full transition-colors ${b.enabled ? 'bg-brand-600' : 'bg-ink-border'}`}
                aria-label={b.enabled ? 'Disable' : 'Enable'}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${b.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}/>
        </button>
      </td>
      <td className="px-3 py-2.5 text-right">
        <button onClick={onEdit} className="text-xs text-ink-muted hover:text-ink-text">Edit</button>
      </td>
    </tr>
  );
}

// ─── Edit modal — GC numbers, manufacturer URL, fetch-PDF-from-URL, scraped PDF list ───
function EditModal({ open, boiler, onClose, onSaved }: { open: boolean; boiler: Boiler | null; onClose: () => void; onSaved: () => void }) {
  const [gcInput, setGcInput] = useState('');
  const [gcs, setGcs] = useState<string[]>([]);
  const [mfrUrl, setMfrUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (boiler) {
      setGcs([...(boiler.gc_numbers || [])]);
      setMfrUrl(boiler.manufacturer_url || '');
      setNotes(boiler.notes || '');
      setGcInput('');
    }
  }, [boiler]);

  if (!boiler) return null;
  const b = boiler;

  function addGc() {
    const v = formatGc(gcInput);
    if (!v) return;
    setGcs(s => [...new Set([...s, v.toUpperCase()])]);
    setGcInput('');
  }
  function removeGc(g: string) { setGcs(s => s.filter(x => x !== g)); }

  async function save() {
    // Auto-commit any pending GC typed but not yet "Added" — common UX confusion
    let finalGcs = gcs;
    const pending = formatGc(gcInput);
    if (pending) finalGcs = [...new Set([...gcs, pending.toUpperCase()])];

    setBusy(true);
    try {
      await aput(`/${b.id}/gc`, { gc_numbers: finalGcs });
      await apatch(`/${b.id}`, { manufacturer_url: mfrUrl || null, notes });
      // Sync local state so the chip appears even if onSaved doesn't reload fast enough
      setGcs(finalGcs);
      setGcInput('');
      toast.push(`Saved · ${finalGcs.length} GC number${finalGcs.length === 1 ? '' : 's'}`, 'success');
      onSaved();
    } catch (e) { toast.push('Save failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function importFromUrl() {
    if (!mfrUrl) { toast.push('Enter a manufacturer PDF URL first', 'error'); return; }
    if (!confirm('Download PDF from URL and attach to this model?')) return;
    setBusy(true);
    try {
      await apost(`/${b.id}/pdf-from-url`, { url: mfrUrl });
      toast.push('PDF downloaded & attached', 'success');
      onSaved();
    } catch (e) { toast.push('Import failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} title={`Edit · ${boiler.manufacturer} ${boiler.model_name}`} onClose={onClose} width={680}>
      <Field label="GC Numbers" hint="UK gas appliance reference (47-044-78 format) — auto-formats">
        <div className="flex gap-2 mb-2">
          <input
            value={gcInput}
            onChange={e => setGcInput(formatGc(e.target.value))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGc(); } }}
            placeholder="47-044-78"
            className={inputCls + ' font-mono'} />
          <button onClick={addGc} type="button"
                  className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-3 py-2 rounded">Add</button>
        </div>
        <div className="flex gap-1.5 flex-wrap min-h-[28px] max-h-[140px] overflow-y-auto">
          {gcs.map(g => (
            <span key={g} className="font-mono text-xs bg-brand-600/15 text-brand-400 rounded px-2 py-1 flex items-center gap-1.5">
              {g}
              <button onClick={() => removeGc(g)} className="text-brand-400/70 hover:text-red-400">×</button>
            </span>
          ))}
          {gcs.length === 0 && <span className="text-xs text-ink-muted">None — engineer can't search this model by GC mark</span>}
        </div>
      </Field>

      <Field label="Manufacturer Manual URL" hint="paste official PDF link, then click 'Download & attach'">
        <div className="flex gap-2">
          <input value={mfrUrl} onChange={e => setMfrUrl(e.target.value)}
                 placeholder="https://www.worcester-bosch.co.uk/.../installation.pdf"
                 className={inputCls} />
          <button onClick={importFromUrl} type="button" disabled={busy || !mfrUrl}
                  className="bg-ink-border hover:bg-ink-hover text-ink-text text-xs font-semibold px-3 py-2 rounded disabled:opacity-50 whitespace-nowrap">
            Download &amp; attach
          </button>
        </div>
      </Field>

      {boiler.scraped_pdfs && boiler.scraped_pdfs.length > 0 && (
        <Field label={`Scraped manuals (${boiler.scraped_pdfs.length})`} hint="auto-found from manufacturer sites — use these to verify the right PDF">
          <div className="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto">
            {boiler.scraped_pdfs.map(p => (
              <div key={p.sha256} className="flex items-center justify-between gap-2 bg-ink-bg/50 border border-ink-border rounded px-2 py-1.5">
                <div className="flex-1 min-w-0">
                  <a href={p.url} target="_blank" rel="noopener" className="text-brand-400 hover:underline text-xs font-mono truncate">
                    {p.sha256.slice(0, 12)}…
                  </a>
                  <div className="text-[10px] text-ink-muted">
                    {p.category} · {p.page_count || '?'} pages
                    {p.gc_numbers.length > 0 && ` · ${p.gc_numbers.length} GCs`}
                  </div>
                </div>
                {p.source_url && (
                  <a href={p.source_url} target="_blank" rel="noopener" className="text-[10px] text-ink-muted hover:text-brand-400 whitespace-nowrap">
                    source ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </Field>
      )}

      <Field label="Internal notes (admin only)">
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={inputCls} />
      </Field>

      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="text-sm text-ink-muted hover:text-ink-text px-3 py-2">Cancel</button>
        <button onClick={save} disabled={busy}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
